import { filterTruthy } from '@procureai/util';
import { commands, MDEditorProps } from '@uiw/react-md-editor';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const {
  bold,
  hr,
  codeEdit,
  codePreview,
  title1,
  title2,
  title3,
  divider,
  link,
  unorderedListCommand,
  orderedListCommand,
} = commands;

export interface UseMarkdownEditorCommandsProps {
  disableEdit?: boolean;
}

export const useMarkdownEditorCommands = ({
  disableEdit,
}: UseMarkdownEditorCommandsProps): Pick<MDEditorProps, 'commands' | 'extraCommands'> => {
  const { t } = useTranslation(undefined, { keyPrefix: 'MarkdownEditor' });
  return useMemo(
    () => ({
      commands: filterTruthy([
        {
          ...bold,
          buttonProps: { title: t('bold.title') },
        },
        {
          ...hr,
          buttonProps: { title: t('hr.title') },
        },
        {
          ...title1,
          buttonProps: { title: t('title1.title') },
          icon: (
            <svg width="12" height="12" viewBox="0 0 12 12">
              <path
                fill="currentColor"
                d="M.536 1.5h1.285v3.857h2.572V1.5h1.286v9H4.393V6.643H1.821V10.5H.536v-9Zm7.071 9V9.214h1.286V2.985l-1.607.926V2.426L8.893 1.5h1.286v7.714h1.285V10.5H7.607Z"
              />
            </svg>
          ),
        },
        {
          ...title2,
          buttonProps: { title: t('title2.title') },
          icon: (
            <svg width="12" height="12" viewBox="0 0 12 12">
              <path
                fill="currentColor"
                d="M.214 1.5H1.5v3.857h2.571V1.5h1.286v9H4.071V6.643H1.5V10.5H.214v-9Zm11.572 9H7.929c-.706 0-1.286-.58-1.286-1.286 0-.34.128-.643.347-.874l3.131-3.362c.238-.232.379-.553.379-.907 0-.705-.58-1.285-1.286-1.285-.705 0-1.285.58-1.285 1.285H6.643A2.584 2.584 0 0 1 9.214 1.5a2.584 2.584 0 0 1 2.572 2.571c0 .708-.29 1.35-.752 1.82L7.929 9.214h3.857V10.5Z"
              />
            </svg>
          ),
        },
        {
          ...title3,
          buttonProps: { title: t('title3.title') },
          icon: (
            <svg width="12" height="12" viewBox="0 0 12 12">
              <path
                fill="currentColor"
                d="M.214 1.5H1.5v3.857h2.571V1.5h1.286v9H4.071V6.643H1.5V10.5H.214v-9Zm7.715 0H10.5c.705 0 1.286.58 1.286 1.286v6.428c0 .706-.581 1.286-1.286 1.286H7.929c-.706 0-1.286-.58-1.286-1.286v-.643h1.286v.643H10.5V6.643H7.929V5.357H10.5V2.786H7.929v.643H6.643v-.643c0-.706.58-1.286 1.286-1.286Z"
              />
            </svg>
          ),
        },
        divider,
        {
          ...link,
          buttonProps: { title: t('link.title') },
        },
        divider,
        {
          ...unorderedListCommand,
          buttonProps: { title: t('unordered-list.title') },
        },
        {
          ...orderedListCommand,
          buttonProps: { title: t('ordered-list.title') },
        },
      ]),
      extraCommands: filterTruthy(
        disableEdit
          ? []
          : [
              {
                ...codeEdit,
                icon: (
                  <svg width="12" height="12" role="img" viewBox="0 0 640 512">
                    <path
                      fill="currentColor"
                      d="M278.9 511.5l-61-17.7c-6.4-1.8-10-8.5-8.2-14.9L346.2 8.7c1.8-6.4 8.5-10 14.9-8.2l61 17.7c6.4 1.8 10 8.5 8.2 14.9L293.8 503.3c-1.9 6.4-8.5 10.1-14.9 8.2zm-114-112.2l43.5-46.4c4.6-4.9 4.3-12.7-.8-17.2L117 256l90.6-79.7c5.1-4.5 5.5-12.3.8-17.2l-43.5-46.4c-4.5-4.8-12.1-5.1-17-.5L3.8 247.2c-5.1 4.7-5.1 12.8 0 17.5l144.1 135.1c4.9 4.6 12.5 4.4 17-.5zm327.2.6l144.1-135.1c5.1-4.7 5.1-12.8 0-17.5L492.1 112.1c-4.8-4.5-12.4-4.3-17 .5L431.6 159c-4.6 4.9-4.3 12.7.8 17.2L523 256l-90.6 79.7c-5.1 4.5-5.5 12.3-.8 17.2l43.5 46.4c4.5 4.9 12.1 5.1 17 .6z"
                    />
                  </svg>
                ),
              },
              {
                ...codePreview,
                icon: (
                  <svg width="12" height="12" role="img" viewBox="0 0 12 12">
                    <path
                      fill="currentColor"
                      d="M6 1.91A6.451 6.451 0 0 1 12 6a6.451 6.451 0 0 1-6 4.09A6.451 6.451 0 0 1 0 6a6.451 6.451 0 0 1 6-4.09Zm0 6.817A2.74 2.74 0 0 0 8.727 6 2.74 2.74 0 0 0 6 3.273 2.74 2.74 0 0 0 3.273 6 2.74 2.74 0 0 0 6 8.727Zm0-4.363c.898 0 1.636.738 1.636 1.636 0 .898-.738 1.636-1.636 1.636A1.644 1.644 0 0 1 4.364 6c0-.898.738-1.636 1.636-1.636Z"
                    />
                  </svg>
                ),
              },
            ],
      ),
    }),
    [disableEdit, t],
  );
};
